import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import { FormSubmission } from '../../types/Form';

interface Lead {
  id: number;
  fullName: string;
  email?: string;
  phone?: string;
  company?: string;
}

type AttachMode = 'single' | 'individual';

interface SubmissionLeadMapping {
  submissionId: number;
  leadId: number | null;
}

interface BulkAttachToLeadModalProps {
  isOpen: boolean;
  selectedSubmissions: FormSubmission[];
  onAttachSingle: (leadId: number) => Promise<void>;
  onAttachIndividual: (mappings: SubmissionLeadMapping[]) => Promise<void>;
  onClose: () => void;
  loadLeads: (search: string) => Promise<Lead[]>;
}

const BulkAttachToLeadModal: React.FC<BulkAttachToLeadModalProps> = ({
  isOpen,
  selectedSubmissions,
  onAttachSingle,
  onAttachIndividual,
  onClose,
  loadLeads
}) => {
  const [mode, setMode] = useState<AttachMode>('single');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [individualMappings, setIndividualMappings] = useState<SubmissionLeadMapping[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAttaching, setIsAttaching] = useState(false);

  const handleLoadLeads = useCallback(async (search: string) => {
    setIsLoading(true);
    try {
      const result = await loadLeads(search);
      setLeads(result);
    } catch (error) {
      console.error('Failed to load leads:', error);
    } finally {
      setIsLoading(false);
    }
  }, [loadLeads]);

  useEffect(() => {
    if (isOpen) {
      handleLoadLeads('');
      // Initialize individual mappings
      setIndividualMappings(
        selectedSubmissions.map(s => ({
          submissionId: s.id,
          leadId: null
        }))
      );
      setMode('single');
      setSelectedLeadId(null);
    }
  }, [isOpen, selectedSubmissions, handleLoadLeads]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    handleLoadLeads(value);
  };

  const handleAttach = async () => {
    if (mode === 'single') {
      if (!selectedLeadId) return;
      setIsAttaching(true);
      try {
        await onAttachSingle(selectedLeadId);
        onClose();
      } catch (error) {
        console.error('Failed to attach submissions:', error);
      } finally {
        setIsAttaching(false);
      }
    } else {
      // Individual mode
      const incompleteMappings = individualMappings.filter(m => m.leadId === null);
      if (incompleteMappings.length > 0) {
        alert(`Please select leads for all ${incompleteMappings.length} submissions`);
        return;
      }
      
      setIsAttaching(true);
      try {
        await onAttachIndividual(individualMappings);
        onClose();
      } catch (error) {
        console.error('Failed to attach submissions:', error);
      } finally {
        setIsAttaching(false);
      }
    }
  };

  const updateIndividualMapping = (submissionId: number, leadId: number) => {
    setIndividualMappings(prev =>
      prev.map(m =>
        m.submissionId === submissionId ? { ...m, leadId } : m
      )
    );
  };

  const getLeadForSubmission = (submissionId: number): Lead | null => {
    const mapping = individualMappings.find(m => m.submissionId === submissionId);
    if (!mapping || !mapping.leadId) return null;
    return leads.find(l => l.id === mapping.leadId) || null;
  };

  const isAttachButtonEnabled = () => {
    if (mode === 'single') {
      return selectedLeadId !== null;
    } else {
      return individualMappings.every(m => m.leadId !== null);
    }
  };

  const handleClose = () => {
    if (isAttaching) return;
    setSearchTerm('');
    setSelectedLeadId(null);
    onClose();
  };

  if (!isOpen) return null;

  const selectedLead = leads.find(l => l.id === selectedLeadId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Attach {selectedSubmissions.length} Submissions to Lead{selectedSubmissions.length > 1 ? 's' : ''}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Choose how to attach the selected submissions
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isAttaching}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Mode Selector */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setMode('single')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === 'single'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Single Lead
              <span className="block text-xs text-gray-500 mt-1">Attach all to one lead</span>
            </button>
            <button
              onClick={() => setMode('individual')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === 'individual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Individual Leads
              <span className="block text-xs text-gray-500 mt-1">Attach each to different lead</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {mode === 'single' ? (
            // Single Lead Mode
            <>
              {/* Info Banner */}
              <div className="px-6 pt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Single Lead Mode:</strong> All {selectedSubmissions.length} submissions will be attached to one lead. 
                    This is useful when the same person submitted multiple forms.
                  </p>
                </div>
              </div>

              {/* Selected Submissions Preview */}
              <div className="px-6 pb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Selected Submissions:</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                  <div className="space-y-1">
                    {selectedSubmissions.map((submission, index) => (
                      <div key={submission.id} className="text-sm text-gray-700 flex items-center justify-between">
                        <span className="truncate">
                          {submission.displayInfo?.name || submission.submitterName || 'Anonymous'} - {submission.formName}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">#{index + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Individual Mode
            <div className="px-6 pt-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-800">
                  <strong>Individual Mode:</strong> Attach each submission to a different lead. 
                  Perfect for processing multiple unique submissions.
                </p>
              </div>
            </div>
          )}

          {mode === 'single' ? (
            // Single Lead Selection
            <>
              {/* Lead Search */}
              <div className="px-6 pb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Select a Lead:</h4>
                <div className="relative mb-3">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search leads by name, email, or company..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Leads List */}
                <div className="border border-gray-300 rounded-lg max-h-80 overflow-y-auto">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : leads.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {searchTerm ? 'No leads found matching your search' : 'No leads available'}
                    </div>
                  ) : (
                    leads.map((lead) => (
                      <div
                        key={lead.id}
                        onClick={() => setSelectedLeadId(lead.id)}
                        className={`px-4 py-3 border-b border-gray-200 last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedLeadId === lead.id ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{lead.fullName}</p>
                            <div className="flex items-center space-x-2 text-sm text-gray-600 mt-1">
                              {lead.email && <span className="truncate">{lead.email}</span>}
                              {lead.company && (
                                <>
                                  <span>•</span>
                                  <span className="truncate">{lead.company}</span>
                                </>
                              )}
                            </div>
                          </div>
                          {selectedLeadId === lead.id && (
                            <CheckCircleIcon className="w-5 h-5 text-blue-600 ml-3 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Selected Lead Preview */}
              {selectedLead && (
                <div className="px-6 pb-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900 font-medium mb-2">
                      {selectedSubmissions.length} submissions will be attached to:
                    </p>
                    <p className="text-sm text-blue-800">
                      <strong>{selectedLead.fullName}</strong>
                      {selectedLead.email && ` (${selectedLead.email})`}
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            // Individual Lead Selection
            <div className="px-6 pb-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Assign Lead for Each Submission:</h4>
              
              {/* Global Search */}
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Individual Mappings */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {selectedSubmissions.map((submission) => {
                  const assignedLead = getLeadForSubmission(submission.id);
                  
                  return (
                    <div key={submission.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                      {/* Submission Info */}
                      <div className="mb-3">
                        <p className="font-medium text-gray-900 text-sm">
                          {submission.displayInfo?.name || submission.submitterName || 'Anonymous'}
                        </p>
                        <p className="text-xs text-gray-600">
                          {submission.displayInfo?.email || submission.submitterEmail} • {submission.formName}
                        </p>
                      </div>

                      {/* Lead Selector */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Attach to:
                        </label>
                        <select
                          value={individualMappings.find(m => m.submissionId === submission.id)?.leadId || ''}
                          onChange={(e) => updateIndividualMapping(submission.id, parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select a lead...</option>
                          {leads.map((lead) => (
                            <option key={lead.id} value={lead.id}>
                              {lead.fullName}{lead.email ? ` (${lead.email})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Assigned Lead Preview */}
                      {assignedLead && (
                        <div className="mt-2 bg-green-50 border border-green-200 rounded p-2">
                          <p className="text-xs text-green-800 flex items-center">
                            <CheckCircleIcon className="w-4 h-4 mr-1" />
                            Will attach to: <strong className="ml-1">{assignedLead.fullName}</strong>
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Summary */}
              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs text-gray-700">
                  Assigned: <strong>{individualMappings.filter(m => m.leadId !== null).length}</strong> of <strong>{selectedSubmissions.length}</strong>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end space-x-3 flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={isAttaching}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAttach}
            disabled={!isAttachButtonEnabled() || isAttaching}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {isAttaching ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Attaching...</span>
              </>
            ) : (
              <>
                <LinkIcon className="w-4 h-4" />
                <span>
                  {mode === 'single' 
                    ? `Attach ${selectedSubmissions.length} to Lead`
                    : `Attach ${selectedSubmissions.length} Submissions`
                  }
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAttachToLeadModal;
